import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, patientAuthApi, type SecurityEvent } from '../api';

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 16,
  padding: 14,
  boxShadow: T.shadow,
  ...extra,
});

const sectionTitle = (text: string) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8 }}>{text}</div>
);

const chevron = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
);

function fmtWhen(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * The account's own security history — sign-ins, password and 2FA changes,
 * devices.
 *
 * The footer of this page has always claimed these are "recorded on your
 * account", with nowhere to read them. `GET /auth/security-events` is that
 * record: every event was written with the patient as the actor, which is
 * exactly why the access log (who ELSE touched the record) could never show
 * them. This account has fifty.
 */
function SecurityActivity() {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [type, setType] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let alive = true;
    patientApi.securityEvents()
      .then((r) => {
        if (!alive) return;
        setEvents(r?.events ?? []);
        setTypes(r?.types ?? []);
        setState('ready');
      })
      .catch(() => { if (alive) setState('failed'); });
    return () => { alive = false; };
  }, []);

  if (state === 'loading') return null;

  if (state === 'failed') {
    return (
      <div style={card()}>
        {sectionTitle('Recent security activity')}
        <div style={{ fontSize: 10, color: T.textTer, fontFamily: T.font }}>
          Your security history could not be loaded just now.
        </div>
      </div>
    );
  }

  const shown = (type ? events.filter((e) => e.type === type) : events).slice(0, showAll ? 50 : 6);

  return (
    <div style={card()}>
      {sectionTitle('Recent security activity')}
      <div style={{ fontSize: 8.5, color: T.textTer, fontFamily: T.font, marginBottom: 10 }}>
        Everything recorded against this account, newest first.
      </div>

      {types.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
          {[null, ...types].map((t) => {
            const on = t === type;
            return (
              <button
                key={t ?? 'all'}
                onClick={() => setType(t)}
                style={{
                  padding: '4px 10px', borderRadius: 14, cursor: 'pointer',
                  fontSize: 9, fontWeight: 700, fontFamily: T.font,
                  color: on ? T.white : T.textSec,
                  background: on ? T.navy : T.white,
                  border: `1px solid ${on ? T.navy : T.border}`,
                }}
              >
                {t ? t.replace(/_/g, ' ') : 'All'}
              </button>
            );
          })}
        </div>
      )}

      {shown.length === 0 ? (
        <div style={{ fontSize: 10, color: T.textTer, fontFamily: T.font }}>Nothing of that kind recorded.</div>
      ) : shown.map((e, i) => (
        <div
          key={e.id}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0',
            borderTop: i > 0 ? `1px solid ${T.border}` : 'none',
          }}
        >
          {/* `ok` is the server's own verdict on the event; a failed sign-in
              is exactly the row a patient is scanning for. */}
          <div style={{
            width: 6, height: 6, borderRadius: 3, marginTop: 5, flexShrink: 0,
            background: e.ok ? '#059669' : T.danger,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.text, fontFamily: T.font }}>{e.title}</div>
            <div style={{ fontSize: 8.5, color: T.textTer, fontFamily: T.font, marginTop: 1 }}>
              {[fmtWhen(e.at), e.device, e.ip, e.detail].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      ))}

      {events.length > 6 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          style={{
            marginTop: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 9.5, fontWeight: 700, color: T.teal, fontFamily: T.font,
          }}
        >
          {showAll ? 'Show fewer' : `Show more (${events.length} recorded)`}
        </button>
      )}
    </div>
  );
}

export default function PatientSecurity() {
  const navigate = useNavigate();
  // Real security state from the server — drives the 2FA ON/OFF badge.
  const [twoFactorOn, setTwoFactorOn] = useState<boolean | null>(null);
  const [sessionCount, setSessionCount] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    patientAuthApi.getSecurityOverview()
      .then((o) => { if (alive) setTwoFactorOn(o.twoFactorEnabled); })
      .catch(() => {});
    patientAuthApi.getSessions()
      .then((rows) => { if (alive) setSessionCount(rows.length); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const items: { label: string; desc: string; color: string; route?: string; badge?: string; badgeColor?: string }[] = [
    { label: 'Change Password', desc: 'Update your login password', color: T.navy, route: '/patient/change-password' },
    {
      label: 'Two-Factor Authentication', desc: 'OTP on top of your password at login', color: T.teal,
      route: '/patient/security/two-factor',
      ...(twoFactorOn !== null
        ? { badge: twoFactorOn ? 'ON' : 'OFF', badgeColor: twoFactorOn ? '#059669' : T.textTer }
        : {}),
    },
    {
      label: 'Active Sessions & Devices', desc: 'See every signed-in device · revoke any of them', color: T.warning,
      route: '/patient/security/sessions',
      ...(sessionCount !== null ? { badge: String(sessionCount), badgeColor: T.navy } : {}),
    },
    { label: 'Biometric Lock', desc: 'Not available in the web app yet — details inside', color: T.purple, route: '/patient/app-lock' },
    { label: 'Privacy Controls', desc: 'Manage data visibility settings', color: T.danger, route: '/patient/consent' },
  ];

  return (
    <div style={{ ...govPageContainer({ maxWidth: 900, paddingBottom: 0 }), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={card()}>
        {sectionTitle('Security & Privacy')}
        {items.map((item, i) => (
          <button
            key={i}
            onClick={() => item.route && navigate(item.route)}
            disabled={!item.route}
            aria-label={item.label}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', width: '100%',
              borderTop: i > 0 ? `1px solid ${T.border}` : 'none',
              cursor: item.route ? 'pointer' : 'default',
              background: 'none', border: i > 0 ? undefined : 'none', textAlign: 'left',
              borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
              opacity: item.route ? 1 : 0.6,
            }}
          >
            <div style={{ width: 30, height: 30, borderRadius: 9, background: `${item.color}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: item.color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: T.text, fontFamily: T.font }}>{item.label}</div>
              <div style={{ fontSize: 8, color: T.textTer, fontFamily: T.font, marginTop: 1 }}>{item.desc}</div>
            </div>
            {item.badge ? (
              <span style={{ fontSize: 7, fontWeight: 700, color: '#FFF', background: item.badgeColor, padding: '2px 8px', borderRadius: 4, fontFamily: T.font }}>{item.badge}</span>
            ) : chevron}
          </button>
        ))}
      </div>

      <SecurityActivity />

      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 12 }}>
        Password changes, 2FA changes and session sign-outs are recorded on your account · NeoHealthCard™
      </div>
    </div>
  );
}
